import { useAuth } from "./context";
import styles from "./GoogleButton.module.css";

/**
 * "Continue with Google" — the same authorization-code + PKCE flow, with Google
 * collecting the credentials instead of Cognito's form.
 *
 * GATED ON CONFIGURATION, and it renders nothing when the flag is off. The
 * provider only exists in an environment whose stack was deployed with Google
 * credentials (see `googleSecretArn` in kev-platform-stack.ts), and a button
 * that hands someone to Cognito for a provider that isn't there produces a raw
 * OAuth error page with our branding on it. No button is better than that.
 *
 * WHAT A GOOGLE USER STILL HAS TO DO. They arrive with an email and nothing
 * else — no date of birth, no country, no T&C acceptance. That is not a gap
 * here: `RequireOnboarded` routes anyone without a consent row to /welcome, and
 * `RequireAdult` refuses anyone without a verified birthdate. So the 18+ gate
 * and the consent record are collected in exactly the same place they are for a
 * password signup, one screen later.
 *
 * WHY IT IS SAFE TO OFFER THIS ON BOTH PAGES. Signing in and signing up are the
 * same button to Google, and the pre-signup trigger links a Google identity to
 * an existing account with the same VERIFIED email rather than creating a second
 * one. Without that linking, someone who signed up with a password and later
 * clicked here would land in an empty account with their subscription invisible.
 */
const ENABLED = import.meta.env.VITE_GOOGLE_ENABLED === "on";

export function GoogleButton({ next, label }: { next: string; label?: string }) {
  const { beginSignIn } = useAuth();
  if (!ENABLED) return null;

  return (
    <button
      type="button"
      className={styles.google}
      onClick={() => {
        /* No consents passed. Google is not where a T&C acceptance is given, and
           recording one from a click that only proves someone has a Google
           account would put a row in the audit trail that proves nothing. */
        void beginSignIn({ intent: "login", next, idp: "Google" });
      }}
    >
      {/* Inline, not an <img>: one fewer request on the critical path, and the
          CSP has no external image host. Google's mark, unmodified — their brand
          guidelines require it be recognisable and not recoloured. */}
      <svg className={styles.mark} viewBox="0 0 18 18" aria-hidden="true">
        <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
        <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
        <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
        <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
      </svg>
      {label ?? "Continue with Google"}
    </button>
  );
}
